/**
 * Delivery History API
 * 배달 내역 조회 API
 *
 * @description
 * 배달 내역 조회, 상태 코드 조회, 협력사 목록 조회 기능 제공
 */
import { apiClient } from '@/shared/api/client';
import { formatDateToYmd } from '@/shared/utils/apiResponseHelpers';
import type { DeliveryHistoryFilter, StatusOption } from '../types/deliveryHistory.types';
import type { DeliveryHistoryApiResponse, PartnerListApiResponse } from '../types/deliveryHistory.api.types';

// ============================================================
// 상수 정의
// ============================================================

/** 배달 API 기본 경로 */
const BASE_PATH = '/api/v1/delivery';

// ============================================================
// API 서비스
// ============================================================

/**
 * 배달 내역 API 서비스
 */
export const deliveryHistoryApi = {
    /**
     * 배달 상태 코드 조회 (드롭박스용)
     * GET /api/v1/delivery/delivery-status/codes
     *
     * @returns 상태 코드 목록
     */
    getStatusCodes: async (): Promise<StatusOption[]> => {
        const response = await apiClient.get<StatusOption[]>(
            `${BASE_PATH}/delivery-status/codes`
        );
        return response.data;
    },

    /**
     * 협력사 목록 조회 (드롭박스용)
     * GET /api/v1/delivery/supplier/list
     *
     * @returns 협력사 목록 응답
     */
    getPartnerList: async (): Promise<PartnerListApiResponse> => {
        const response = await apiClient.get<PartnerListApiResponse>(
            `${BASE_PATH}/supplier/list`
        );
        return response.data;
    },

    /**
     * 배달 내역 조회
     * GET /api/v1/delivery/history
     *
     * @param filter 조회 필터 조건
     * @returns 배달 내역 응답
     */
    getDeliveryHistory: async (
        filter: DeliveryHistoryFilter
    ): Promise<DeliveryHistoryApiResponse> => {
        // 파라미터 빌드 (빈 값 제거)
        const params: Record<string, string> = {};

        if (filter.partnerId) params.suppId = filter.partnerId;
        if (filter.riderId) params.riderId = filter.riderId;
        if (filter.startDate) params.fromDate = formatDateToYmd(filter.startDate);
        if (filter.endDate) params.toDate = formatDateToYmd(filter.endDate);
        if (filter.status) params.deliveryStatus = filter.status;

        const response = await apiClient.get<DeliveryHistoryApiResponse>(
            `${BASE_PATH}/history`,
            { params }
        );
        return response.data;
    }
};
